import React from 'react';
import {
    Button,
    FormControl,
    FormGroup,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField
} from '@mui/material';
import EventSequenceSummary, {PhaseOfFlight} from '../../../../@types/event-sequence-summary';
import {uuid} from '../../../../utils/utils';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import Checkbox from '@mui/material/Checkbox';
import EventSequence from '../../../../@types/event-sequence';
import {ResourceType} from '../../../../@types/resource-type';
import SafetyModel from '../../../../@types/safety-model';
import EventSequenceService from '../../../../services/event-sequence.service';
import SafetyModelService from '../../../../services/safety.service';
import {EventSequenceSummaryEditorProps} from '../../../../@types/editor-props';
import {hasRightToEdit} from '../../../../utils/role-to-rights';
import {ResourceAction} from '../../../../@types/resource-action';
import {toast} from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';


interface QuantificationMode {
    modeName: string;
    modeShortName: string;
}

const quantificationModes: QuantificationMode[] = [
    { modeName: 'Frequency Mode', modeShortName: 'MODE_FREQUENCY' },
    { modeName: 'Probability Mode', modeShortName: 'MODE_PROBABILITY' },
];

const EventSequenceSummaryEditor: React.FC<EventSequenceSummaryEditorProps> = (props: EventSequenceSummaryEditorProps) => {
    const { eventSequenceSummary, isActive, updateTab } = props;
    const [name, setName] = React.useState<string>(eventSequenceSummary?.name || '');
    const [uniqueId, setUniqueId] = React.useState<string>(eventSequenceSummary?.uniqueId || '');
    const [description, setDescription] = React.useState<string>(eventSequenceSummary?.description || '');
    const [checkedFlightPhases, setCheckedFlightPhases] = React.useState<PhaseOfFlight[]>(
        Array.isArray(eventSequenceSummary?.phasesOfFlight) ? [...eventSequenceSummary.phasesOfFlight] : []
    );
    const [exposure, setExposure] = React.useState<string>(eventSequenceSummary?.exposureFactor?.toString() || '1');
    const [quantificationMode, setQuantificationMode] = React.useState<string>(
        eventSequenceSummary?.quantificationMode || quantificationModes[1].modeShortName
    );
    const [error, setError] = React.useState<string | null>(null);
    const [editMode, setEditMode] = React.useState(!!eventSequenceSummary?.id);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    
    // Store the initial editable state to prevent it from changing after save
    const [isEditable] = React.useState<boolean>(() => {
        if (!eventSequenceSummary.id) return true; // New event sequences are always editable
        return hasRightToEdit({objectType: ResourceType.EVENT_SEQUENCES, object: eventSequenceSummary}, undefined);
    });
    
    const uuidRef = React.useRef(uuid());
    const { workspaceObjects, setWorkspaceObjects, setCurrentWorkspaceObject, getAdditionalResource, currentAction, setCurrentAction, phasesOfFlight, publishResourceUpdate } = useWorkspaceContext();

    const onUniqueIdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setUniqueId(event.target.value);
    };

    const onNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescription(event.target.value);
    };

    const onExposureChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setExposure(event.target.value);
    };

    const isNotSavable = (): boolean => {
        // Use the stored editable state instead of recalculating permissions
        // This prevents the form from becoming read-only after save when the API returns incomplete group data
        return eventSequenceSummary.id !== null && eventSequenceSummary.id !== undefined && !isEditable;
    }

    const onExposureChangeBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        if (+event.target.value > 1) {
            // remove leading zeros
            setExposure(event.target.value.replace(/^0+/, ''));
        }
    }

    const onQuantificationModeChange = (event: any): void => {
        setQuantificationMode(event.target.value);
    };

    const handleFlightPhaseToggle = (phaseName: string) => (): void => {
        const currentIndex = checkedFlightPhases.findIndex(element => element.phaseName === phaseName);
        const newChecked = [...checkedFlightPhases];

        if (currentIndex === -1) {
            let currentItem = phasesOfFlight?.find(element => element.phaseName === phaseName);
            if (currentItem) {
                newChecked.push(currentItem);
            }
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setCheckedFlightPhases(newChecked);
    }

    const formEventSequence = (): EventSequence => {
        const eventSequenceSummaryData = {} as EventSequence;
        eventSequenceSummaryData.uniqueId = uniqueId ?? '';
        eventSequenceSummaryData.name = name ?? '';
        eventSequenceSummaryData.description = description;
        eventSequenceSummaryData.exposureFactor = +exposure > 0 ? +exposure : 1;
        eventSequenceSummaryData.quantificationMode = quantificationMode;
        eventSequenceSummaryData.phasesOfFlight = checkedFlightPhases;

        if (eventSequenceSummary && eventSequenceSummary.id) {
            eventSequenceSummaryData.id = eventSequenceSummary.id;
        }
        return eventSequenceSummaryData;
    }

    const updateEventSequence = (eventSequence: EventSequence): void => {
        // The API returns incomplete group data without currentUser info
        // We need to preserve the role from the original eventSequenceSummary
        if (!eventSequence.role && eventSequenceSummary.role) {
            eventSequence.role = eventSequenceSummary.role;
        }
        
        let eventSequenceObject = { objectType: ResourceType.EVENT_SEQUENCES, object: eventSequence };
        setCurrentWorkspaceObject(eventSequenceObject);
        if (workspaceObjects?.objectType === ResourceType.SAFETY_MODELS) {
            let resourcesObject = getAdditionalResource(ResourceType.SAFETY_MODELS);
            if (resourcesObject && resourcesObject.resources) {
                let resources = [...resourcesObject.resources] as SafetyModel[];
                let sm = resources.find((resource) => resource.id == eventSequenceSummary.containerId);
                if (sm) {
                    if (editMode) {
                        // Ensure children array exists before accessing it
                        if (!sm.children) {
                            sm.children = [] as EventSequenceSummary[];
                        }
                        let index = sm.children.findIndex((es) => es.id === eventSequence.id);
                        if (index !== -1) {
                            sm.children[index] = eventSequence;
                        }
                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
                    } else {
                        if (!sm.children) {
                            sm.children = [] as EventSequenceSummary[];
                            
                        }
                        sm.children.push(eventSequence);
                        sm.numberEventSequences = sm.children.length;
                        sm.children.sort((a, b) => a.uniqueId.localeCompare(b.uniqueId) || a.name.localeCompare(b.name));
                        setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS, resources: resources });
                    }
                }
            } else {
                setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
            }
        } 
        
        // eslint-disable-next-line no-console
        // console.log('EventSequenceSummaryEditor: Publishing update event for:', eventSequence);
        
        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.EVENT_SEQUENCES,
            id: eventSequence.id,
            updatedData: eventSequence,
            source: 'save'
        });
        
        updateTab(eventSequenceObject);
    }

    const handleSave = (): void => {       
        if (name && uniqueId) {
            setError(null);
            if (editMode) {
                let eventSequence = formEventSequence();
                setIsRunning(true);
                EventSequenceService.updateEventSequence(eventSequence)
                    .then((eventSequence: EventSequence) => {
                        updateEventSequence(eventSequence);
                        toast.success('Safety View updated successfully');
                    })
                    .catch((error) => {
                        setError(error.message);
                        toast.error('Error updating Safety View');
                    }).finally(() => {
                        setIsRunning(false);
                    });
            } else {
                let eventSequence = formEventSequence();
                setIsRunning(true);
                SafetyModelService.addEventSequenceToSafetyModel(eventSequenceSummary.containerId, eventSequence)
                    .then((eventSequence: EventSequence) => {
                        updateEventSequence(eventSequence);
                        setEditMode(true);
                        toast.success('Safety View created successfully');
                    })
                    .catch((error) => {
                        setError(error.message);
                        toast.error('Error creating Safety View');
                    }).finally(() => {
                        setIsRunning(false);
                    });
            }
        }
    };

    // Track the current event sequence ID to detect when editing a different one
    const currentEventSequenceIdRef = React.useRef(eventSequenceSummary?.id);
    
    React.useEffect(() => {
        if (eventSequenceSummary) {
            // Only reset form values if we're editing a different event sequence
            currentEventSequenceIdRef.current = eventSequenceSummary.id;
            if (eventSequenceSummary.id) {
                setEditMode(true);
                setUniqueId(eventSequenceSummary.uniqueId ?? '');
                setName(eventSequenceSummary.name ?? '');
                setDescription(eventSequenceSummary.description || '');
                setExposure(eventSequenceSummary.exposureFactor.toString() ?? '1');
                if (eventSequenceSummary.quantificationMode) {
                    setQuantificationMode(eventSequenceSummary.quantificationMode);
                }
                const phases = Array.isArray(eventSequenceSummary.phasesOfFlight) ?
                    [...eventSequenceSummary.phasesOfFlight]
                    : [] as PhaseOfFlight[];
                setCheckedFlightPhases(phases);
            }
        }
        if (isActive && currentAction === ResourceAction.SAVE) {
            setCurrentAction(null);
            handleSave();
        }
    }, [eventSequenceSummary, currentAction]);

    return (
        <FormGroup className='formStyle'>
            <Stack className='formControl'>
                <TextField
                    label='Unique ID'
                    aria-label='unique id'
                    type='text'
                    variant="outlined"
                    size='small'
                    id={`unique-id-input-${uuidRef.current}`}
                    autoComplete='on'
                    value={uniqueId || ''}
                    onChange={onUniqueIdChange}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `unique-id-input-${uuidRef.current}`,
                            'aria-label': 'unique id'
                        }
                    }}
                    disabled={isNotSavable()}
                    fullWidth
                />
                <hr />
                <TextField
                    label='Name'
                    aria-label='name'
                    type='text'
                    variant="outlined"
                    size='small'
                    id={`name-input-${uuidRef.current}`}
                    autoComplete='on'
                    value={name || ''}
                    onChange={onNameChange}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `name-input-${uuidRef.current}`,
                            'aria-label': 'name'
                        }
                    }}
                    disabled={isNotSavable()}
                    fullWidth
                />                
                <hr />
                <TextField
                    label='Description'
                    aria-label='description'
                    type='text'
                    variant="outlined"
                    size='small'
                    id={`description-input-${uuidRef.current}`}
                    autoComplete='on'
                    value={description || ''}
                    onChange={onDescriptionChange}
                    minRows={3}
                    multiline
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `description-input-${uuidRef.current}`,
                            'aria-label': 'description'
                        }
                    }}
                    disabled={isNotSavable()}
                    fullWidth
                />
                <hr />
                <Stack direction="row" spacing={2} justifyContent='start'>
                    <FormControl sx={{ width: '30%' }}>
                        <TextField
                            error={exposure === '' || +exposure <= 0}
                            helperText={exposure === '' || +exposure <= 0 ? 'Exposure Factor must be greater than 0' : ''}
                            label='Exposure Factor'
                            aria-label='exposure'
                            type='number'
                            variant="outlined"
                            size='small'
                            id={`exposure-input-${uuidRef.current}`}
                            value={exposure}
                            onChange={onExposureChange}
                            onBlur={onExposureChangeBlur}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `exposure-input-${uuidRef.current}`,
                                    'aria-label': 'exposure',
                                },
                                htmlInput: { step: '0.1' },
                                inputLabel: { shrink: true }
                       
                            }}
                            disabled={isNotSavable()}
                            fullWidth
                        />
                    </FormControl>
                    <FormControl  sx={{ width: '30%' }}>
                        <InputLabel tabIndex={-1} id={`select-mode-label-${uuidRef.current}`} htmlFor={`select-mode-${uuidRef.current}`}>Quantification Mode</InputLabel>
                        <Select
                            role='presentation'
                            labelId={`select-mode-label-${uuidRef.current}`}
                            id={`mode-${uuidRef.current}`}
                            size='small'
                            label='Quantification Mode'
                            value={quantificationMode}
                            onChange={onQuantificationModeChange}
                            inputProps={{
                                'role': 'textbox',
                                'tabIndex': 0,
                                'id': `select-mode-${uuidRef.current}`,
                            }}
                            disabled={isNotSavable()}
                        >
                            {quantificationModes.map((mode: QuantificationMode) => (
                                <MenuItem key={mode.modeShortName} value={mode.modeShortName}>{mode.modeName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </Stack>
                <hr />
                <fieldset style={{ border: '1px solid #ccc', borderRadius: '4px' }} disabled={isNotSavable()}>
                    <legend style={{
                        fontFamily: 'Roboto, Helvetica',
                        fontSize: '0.8rem',
                        fontWeight: 400,
                        lineHeight: 1.4,
                        color: 'rgba(0, 0, 0, 0.6)',
                        paddingLeft: '0.2rem',
                    }}>Phases of Flight</legend>                 
                    <List
                        id={`select-list-${uuidRef.current}`}
                        aria-labelledby={`select-label-${uuidRef.current}`}
                        sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                        }}
                        dense
                    >
                        {phasesOfFlight && phasesOfFlight.map((phase) => {
                            const labelId = `checkbox-list-label-${phase.phaseName}`;
                            return (
                                <ListItem
                                    key={phase.phaseName}
                                    dense
                                >
                                    <ListItemButton role={undefined} onClick={handleFlightPhaseToggle(phase.phaseName)} dense disabled={isNotSavable()}>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={checkedFlightPhases.findIndex(element => element.phaseName === phase.phaseName) !== -1}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={labelId} primary={phase.phaseName} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>           
                </fieldset>
                <hr />
                <div>
                    <Button className='formBtn' id={`save-safety-model-${uuidRef.current}`}
                        size='small' variant='contained'
                        aria-label='save safety model'
                        title='Save Safety Model'
                        onClick={handleSave}
                        startIcon={isRunning ? <CircularProgress size={20} /> : <SaveAltIcon />}
                        disabled={isNotSavable() || name.trim() === '' || uniqueId.trim() === ''}
                    >Save</Button>
                </div>
                {error ? <ErrorAlertComponent message={error} />  : null}
            </Stack>
        </FormGroup>
    );
};

export default EventSequenceSummaryEditor;
